import React, { useState, useRef, useCallback } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Wordmark, CrestMark } from "@/components/Wordmark";
import { SouqMotif } from "@/components/marketing/MarketingPrimitives";
import { toast } from "sonner";
import {
  FileKey2, ShieldCheck, Lock, FileCheck2, ScrollText, UploadCloud,
  KeyRound, Download, ArrowRight, Loader2, CheckCircle2, AlertTriangle,
  ArrowLeft, Fingerprint,
} from "lucide-react";

const HERO_IMG = null; // external image removed — replaced with self-contained luxury backdrop

// quick non-cryptographic checksum just for the UI preview (first 8 chars)
function previewChecksum(text) {
  let h = 0;
  for (let i = 0; i < text.length; i++) {
    h = (h << 5) - h + text.charCodeAt(i);
    h |= 0;
  }
  return (h >>> 0).toString(16).padStart(8, "0").slice(0, 8);
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const fileRef = useRef(null);

  const [fileName, setFileName] = useState("");
  const [tokenText, setTokenText] = useState("");
  const [status, setStatus] = useState("idle"); // idle | selected | verifying | success | error
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  // setup tab
  const [setupEmail, setSetupEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [provisioned, setProvisioned] = useState(null);

  const readFile = useCallback((f) => {
    if (!f) return;
    setError("");
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setTokenText(String(ev.target.result || ""));
      setStatus("selected");
    };
    reader.readAsText(f);
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  const handleFile = (e) => readFile(e.target.files?.[0]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    readFile(e.dataTransfer.files?.[0]);
  };

  const doLogin = async (text) => {
    const payload = text ?? tokenText;
    if (!payload) {
      setError("Please select your .fokey key file to continue.");
      setStatus("error");
      return;
    }
    setStatus("verifying");
    setError("");
    setProgress(20);
    const tick = setInterval(() => setProgress((p) => Math.min(p + 18, 85)), 180);
    try {
      await login(payload);
      clearInterval(tick);
      setProgress(100);
      setStatus("success");
      toast.success("Identity verified. Welcome back.");
      setTimeout(() => navigate("/dashboard"), 600);
    } catch (err) {
      clearInterval(tick);
      setProgress(0);
      const msg = err?.response?.data?.detail || "We could not verify this key file.";
      setError(msg);
      setStatus("error");
      toast.error(msg);
    }
  };

  const doProvision = async () => {
    if (!setupEmail) {
      toast.error("Enter the enrolled email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/provision-token", { email: setupEmail.trim() });
      setProvisioned(data);
      const blob = new Blob([data.token], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Key file generated & downloaded. Store it on your secure drive.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate key file.");
    } finally {
      setBusy(false);
    }
  };

  const verifying = status === "verifying";
  const success = status === "success";

  return (
    <div className="theme-marketing-dark min-h-screen font-sans antialiased lg:grid lg:grid-cols-2">
      {/* Left cinematic panel */}
      <div className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12 mk-wash tex-grain">
        <SouqMotif />
        {/* Self-contained luxury vault backdrop (no external image) */}
        <div className="pointer-events-none absolute inset-0 opacity-90"
          style={{ background: "radial-gradient(680px 420px at 30% 20%, rgba(176,141,87,0.16), transparent 60%), radial-gradient(520px 520px at 85% 90%, rgba(15,61,46,0.22), transparent 65%)" }} />
        <div className="pointer-events-none absolute -right-24 top-1/2 hidden -translate-y-1/2 opacity-[0.07] xl:block">
          <CrestMark size={460} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#070A0F] via-[#070A0F]/45 to-transparent" />

        <div className="relative">
          <Link to="/" className="focus-brass rounded-md">
            <Wordmark size="md" tone="light" rule />
          </Link>
        </div>

        <motion.div
          className="relative max-w-md"
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1 className="font-display text-4xl font-semibold leading-[1.08] tracking-[-0.02em] text-[color:var(--mk-text)] xl:text-5xl">
            Your key opens the whole house.
          </h1>
          <p className="mt-5 text-[15px] leading-7 text-[color:var(--mk-text-2)]">
            Family Souq is unlocked by a private key file only you hold — verified locally,
            never stored. Insert your key to enter the vault.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3">
            {[
              { icon: Lock, label: "Encrypted key" },
              { icon: FileCheck2, label: "Verified locally" },
              { icon: ScrollText, label: "Full audit trail" },
            ].map((t) => (
              <div
                key={t.label}
                className="flex flex-col items-center gap-2 rounded-xl border border-[color:var(--mk-border)] bg-white/5 py-4"
              >
                <t.icon className="h-5 w-5 text-[color:var(--mk-primary)]" />
                <span className="text-center text-[11px] text-[color:var(--mk-text-2)]">{t.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="relative text-xs text-[color:var(--mk-muted)]">
          Private-bank-grade confidentiality for Indian family offices.
        </div>
      </div>

      {/* Right auth panel */}
      <div className="relative flex min-h-screen items-center justify-center bg-[color:var(--mk-bg-2)] p-6 sm:p-10 lg:min-h-0">
        <div className="w-full max-w-md">
          {/* mobile brand + back */}
          <div className="mb-8 flex items-center justify-between lg:hidden">
            <Link to="/"><Wordmark size="md" tone="light" /></Link>
          </div>
          <Link
            to="/"
            className="mb-6 hidden items-center gap-2 text-xs text-[color:var(--mk-muted)] transition-colors hover:text-[color:var(--mk-text-2)] lg:inline-flex"
            data-testid="login-back-home"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="mb-6 grid w-full grid-cols-2 bg-[color:var(--mk-surface)]">
              <TabsTrigger value="login" data-testid="login-tab-sign-in">Sign in</TabsTrigger>
              <TabsTrigger value="setup" data-testid="login-tab-first-time-setup">First-time setup</TabsTrigger>
            </TabsList>

            {/* SIGN IN */}
            <TabsContent value="login">
              <div className="mk-card p-6 sm:p-8">
                <div className="mb-1 flex items-center gap-2 text-[color:var(--mk-primary-2)]">
                  <Fingerprint className="h-5 w-5" />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em]">Key-file sign-in</span>
                </div>
                <h2 className="font-display text-2xl font-semibold text-[color:var(--mk-text)]">Insert your key</h2>
                <p className="mb-6 mt-1 text-sm text-[color:var(--mk-text-2)]">
                  Drop or choose your <span className="font-medium text-[color:var(--mk-text)]">.fokey</span> key
                  file to unlock the vault.
                </p>

                <input
                  ref={fileRef} type="file" accept=".fokey,.json,.txt,application/json"
                  className="hidden" onChange={handleFile} data-testid="login-token-file-input"
                />

                {/* Dropzone / states */}
                {!success && (
                  <div
                    role="button"
                    tabIndex={0}
                    data-testid="login-token-dropzone"
                    onClick={() => !verifying && fileRef.current?.click()}
                    onKeyDown={(e) => e.key === "Enter" && fileRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={
                      "focus-brass flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed py-7 transition-colors " +
                      (verifying ? "cursor-default " : "cursor-pointer ") +
                      (dragOver
                        ? "border-[color:var(--mk-primary)] bg-[color:var(--mk-primary)]/10"
                        : "border-[color:var(--mk-border-strong)] bg-[color:var(--mk-bg-2)] hover:border-[color:var(--mk-primary)] hover:bg-white/5")
                    }
                  >
                    {status === "selected" || verifying ? (
                      <>
                        <FileCheck2 className="h-7 w-7 text-[color:var(--mk-primary)]" />
                        <span className="text-sm font-medium text-[color:var(--mk-text)]">{fileName || "Key selected"}</span>
                        <span className="text-[11px] text-[color:var(--mk-muted)]">
                          checksum · {previewChecksum(tokenText)}
                        </span>
                      </>
                    ) : (
                      <>
                        <FileKey2 className="h-7 w-7 text-[color:var(--mk-primary)]" />
                        <span className="text-sm font-medium text-[color:var(--mk-text)]">
                          Drag &amp; drop your key file
                        </span>
                        <span className="text-[11px] text-[color:var(--mk-muted)]">or click to browse</span>
                      </>
                    )}
                  </div>
                )}

                {/* Verifying progress */}
                {verifying && (
                  <div className="mt-4" data-testid="login-verifying">
                    <Progress value={progress} className="h-1.5 bg-[color:var(--mk-bg-2)]" />
                    <p className="mt-2 text-xs text-[color:var(--mk-text-2)]">Verifying key signature…</p>
                  </div>
                )}

                {/* Success */}
                {success && (
                  <Alert className="mt-1 border-[color:var(--mk-primary)]/40 bg-[color:var(--mk-primary)]/10" data-testid="login-success">
                    <CheckCircle2 className="h-4 w-4 text-[color:var(--mk-primary-2)]" />
                    <AlertDescription className="text-[color:var(--mk-text-2)]">
                      Verified. Opening your vault…
                    </AlertDescription>
                  </Alert>
                )}

                {/* Error */}
                {status === "error" && error && (
                  <Alert variant="destructive" className="mt-4 border-[#B23A3A]/40 bg-[#B23A3A]/10" data-testid="login-error-message">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-[#E9B7B7]">{error}</AlertDescription>
                  </Alert>
                )}

                {!success && (
                  <Button
                    onClick={() => doLogin()}
                    disabled={verifying || !tokenText}
                    data-testid="login-submit-button"
                    className="mt-5 h-11 w-full bg-[color:var(--mk-primary)] text-[#120E08] hover:bg-[color:var(--mk-primary-2)]"
                  >
                    {verifying ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <><ShieldCheck className="mr-2 h-4 w-4" /> Unlock vault</>
                    )}
                  </Button>
                )}
              </div>
            </TabsContent>

            {/* SETUP */}
            <TabsContent value="setup">
              <div className="mk-card p-6 sm:p-8">
                <div className="mb-1 flex items-center gap-2 text-[color:var(--mk-primary-2)]">
                  <KeyRound className="h-5 w-5" />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em]">Provision a key</span>
                </div>
                <h2 className="font-display text-2xl font-semibold text-[color:var(--mk-text)]">Generate your key file</h2>
                <p className="mb-6 mt-1 text-sm text-[color:var(--mk-text-2)]">
                  Enter your enrolled email to generate an encrypted{" "}
                  <span className="font-medium text-[color:var(--mk-text)]">.fokey</span> file. Store it on your
                  secure drive — it is your key.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="setup-email" className="text-[color:var(--mk-text-2)]">Enrolled email</Label>
                  <Input
                    id="setup-email" type="email" placeholder="admin@familyoffice.in"
                    value={setupEmail} onChange={(e) => setSetupEmail(e.target.value)}
                    data-testid="setup-email-input"
                    className="bg-[color:var(--mk-bg-2)] border-[color:var(--mk-border-strong)] text-[color:var(--mk-text)] placeholder:text-[color:var(--mk-muted)]"
                  />
                  <p className="text-[11px] text-[color:var(--mk-muted)]">
                    First-time? Use the bootstrap admin email configured for your office.
                  </p>
                </div>

                <Button
                  onClick={doProvision} disabled={busy}
                  data-testid="setup-generate-button"
                  className="mt-5 h-11 w-full border border-[color:var(--mk-border-strong)] bg-transparent text-[color:var(--mk-text)] hover:bg-white/5"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="mr-2 h-4 w-4" /> Generate &amp; download key</>}
                </Button>

                {provisioned && (
                  <div className="mt-5 rounded-xl border border-[color:var(--mk-primary)]/30 bg-[color:var(--mk-primary)]/[0.08] p-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-[color:var(--mk-primary-2)]">
                      <FileCheck2 className="h-4 w-4" /> Key generated for {provisioned.user?.name}
                    </div>
                    <p className="mt-1 text-xs text-[color:var(--mk-text-2)]">
                      Saved as <span className="font-medium text-[color:var(--mk-text)]">{provisioned.filename}</span>.
                      Keep it on your secure drive. You can sign in instantly below.
                    </p>
                    <Button
                      onClick={() => doLogin(provisioned.token)} disabled={verifying}
                      data-testid="setup-login-now-button"
                      className="mt-3 h-10 w-full bg-[color:var(--mk-primary)] text-[#120E08] hover:bg-[color:var(--mk-primary-2)]"
                    >
                      Continue to vault <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}

                <div className="mt-6 flex items-start gap-2 text-[11px] text-[color:var(--mk-muted)]">
                  <UploadCloud className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>The key is encrypted (Fernet) and signed (JWT). It can only be verified by this vault's server keys.</span>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
